import { useState, useEffect, useMemo } from 'react'
import { subscribeToContacts } from '../firebase/contacts'
import { subscribeToDeals, subscribeToAllDeals, getOrCreatePipeline } from '../firebase/deals'
import { getUserActivities } from '../firebase/activities'
import { subscribeToProspects } from '../firebase/prospects'
import { subscribeToCompanyProspects } from '../firebase/companyProspects'
import { subscribeToJobRoleAnalysis } from '../firebase/jobRoleAnalysis'
import './Analytics.css'

const Analytics = ({ user, isAdmin = false }) => {
  const [contacts, setContacts] = useState([])
  const [deals, setDeals] = useState([])
  const [activities, setActivities] = useState([])
  const [prospects, setProspects] = useState([])
  const [companyProspects, setCompanyProspects] = useState([])
  const [jobRoleAnalysis, setJobRoleAnalysis] = useState([])
  const [pipeline, setPipeline] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!user) return

    // Subscribe to contacts
    const unsubscribeContacts = subscribeToContacts(user.uid, (contactsData) => {
      setContacts(contactsData)
    })

    const unsubscribeDeals = isAdmin
      ? subscribeToAllDeals((dealsData) => {
          setDeals(dealsData)
        })
      : subscribeToDeals(user.uid, (dealsData) => {
          setDeals(dealsData)
        })

    // Subscribe to prospects (shared resource)
    const unsubscribeProspects = subscribeToProspects((prospectsData) => {
      setProspects(prospectsData)
    })

    // Subscribe to company prospects (shared resource)
    const unsubscribeCompanyProspects = subscribeToCompanyProspects((companiesData) => {
      setCompanyProspects(companiesData)
    })

    // Subscribe to job role analysis (shared resource)
    const unsubscribeJobRoleAnalysis = subscribeToJobRoleAnalysis((jobsData) => {
      setJobRoleAnalysis(jobsData)
    })

    // Load pipeline
    const loadPipeline = async () => {
      try {
        const pipelineData = await getOrCreatePipeline(user.uid)
        setPipeline(pipelineData)
      } catch (error) {
        console.error('Error loading pipeline:', error)
      }
    }

    loadPipeline()

    // Load activities
    const loadActivities = async () => {
      try {
        const activitiesData = await getUserActivities(user.uid)
        setActivities(activitiesData)
        setLoading(false)
      } catch (error) {
        console.error('Error loading activities:', error)
        setLoading(false)
      }
    }

    loadActivities()

    return () => {
      unsubscribeContacts()
      unsubscribeDeals()
      unsubscribeProspects()
      unsubscribeCompanyProspects()
      unsubscribeJobRoleAnalysis()
    }
  }, [user, isAdmin])

  const stagesForDealsReporting = useMemo(() => {
    if (!pipeline?.stages?.length) return []
    const knownIds = new Set(pipeline.stages.map((s) => s.id))
    const extraIds = [...new Set(deals.map((d) => d.stage).filter(Boolean))].filter((id) => !knownIds.has(id))
    const extras = extraIds.map((id) => ({
      id,
      name: id.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
    }))
    return [...pipeline.stages, ...extras]
  }, [pipeline, deals])

  // Calculate metrics
  const totalContacts = contacts.length
  const totalDeals = deals.length
  const totalActivities = activities.length

  // Deal value calculations
  const totalDealValue = deals.reduce((sum, deal) => sum + (deal.value || 0), 0)
  const dealsWithValue = deals.filter(deal => deal.value && deal.value > 0)
  const averageDealValue = dealsWithValue.length > 0 
    ? totalDealValue / dealsWithValue.length 
    : 0

  // Deal value by stage (include stages only present on other users’ deals when admin)
  const dealValueByStage = stagesForDealsReporting.map((stage) => {
    const stageDeals = deals.filter(deal => deal.stage === stage.id)
    const stageValue = stageDeals.reduce((sum, deal) => sum + (deal.value || 0), 0)
    return {
      stageName: stage.name,
      count: stageDeals.length,
      value: stageValue
    }
  })

  // Top deals by value
  const topDeals = [...deals]
    .filter(deal => deal.value && deal.value > 0)
    .sort((a, b) => (b.value || 0) - (a.value || 0))
    .slice(0, 5)

  // Activity breakdown
  const activityBreakdown = activities.reduce((acc, activity) => {
    const type = activity.type || 'other'
    acc[type] = (acc[type] || 0) + 1
    return acc
  }, {})

  // Contacts by tag (all tags, not just top 5)
  const contactsByTag = contacts.reduce((acc, contact) => {
    const tags = contact.tags || []
    if (tags.length === 0) {
      acc['No Tags'] = (acc['No Tags'] || 0) + 1
    } else {
      tags.forEach(tag => {
        acc[tag] = (acc[tag] || 0) + 1
      })
    }
    return acc
  }, {})

  const allTags = Object.entries(contactsByTag)
    .sort(([, a], [, b]) => b - a)

  // Contacts by LinkedIn status
  const contactsByLinkedInStatus = contacts.reduce((acc, contact) => {
    const status = contact.linkedinStatus || 'not_started'
    acc[status] = (acc[status] || 0) + 1
    return acc
  }, {})

  const getLinkedInStatusLabel = (status) => {
    const labels = {
      'not_started': 'Not Started',
      'initial_sent': 'Initial Sent',
      'followup_sent': 'Follow-up Sent',
      'replied': 'Replied',
      'value_offered': 'Value Offered',
      'converted': 'Converted'
    }
    return labels[status] || status
  }

  const getLinkedInStatusColor = (status) => {
    const colors = {
      'not_started': '#6b7280',
      'initial_sent': '#3b82f6',
      'followup_sent': '#8b5cf6',
      'replied': '#10b981',
      'value_offered': '#f59e0b',
      'converted': '#059669'
    }
    return colors[status] || '#6b7280'
  }

  // Recent activity (last 7 days)
  const sevenDaysAgo = new Date()
  sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7)
  const recentActivities = activities.filter(activity => {
    if (!activity.createdAt) return false
    const activityDate = activity.createdAt.toDate ? activity.createdAt.toDate() : new Date(activity.createdAt)
    return activityDate >= sevenDaysAgo
  }).length

  // Helper function to parse Firestore timestamps
  const parseDate = (timestamp) => {
    if (!timestamp) return null
    return timestamp.toDate ? timestamp.toDate() : new Date(timestamp)
  }

  // Calculate average per day for a collection
  const calculateAveragePerDay = (items) => {
    if (!items || items.length === 0) return 0
    
    const itemsWithDates = items
      .map(item => ({
        ...item,
        date: parseDate(item.createdAt)
      }))
      .filter(item => item.date !== null)
    
    if (itemsWithDates.length === 0) return 0
    
    const dates = itemsWithDates.map(item => item.date.getTime())
    const oldestDate = new Date(Math.min(...dates))
    const newestDate = new Date(Math.max(...dates))
    const today = new Date()
    
    // Use the more recent of newestDate or today
    const endDate = newestDate > today ? newestDate : today
    
    // Calculate days difference (at least 1 day to avoid division by zero)
    const daysDiff = Math.max(1, Math.ceil((endDate - oldestDate) / (1000 * 60 * 60 * 24)))
    
    return (itemsWithDates.length / daysDiff).toFixed(1)
  }

  // Calculate averages per day
  const avgProspectsPerDay = calculateAveragePerDay(prospects)
  const avgCompanyProspectsPerDay = calculateAveragePerDay(companyProspects)
  const avgJobRoleAnalysisPerDay = calculateAveragePerDay(jobRoleAnalysis)

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value || 0)
  }

  const getActivityIcon = (type) => {
    const icons = {
      'email': '📧',
      'call': '📞',
      'meeting': '🤝',
      'website_visit': '🌐',
      'linkedin_message': '💼'
    }
    return icons[type] || '📝'
  }

  if (loading) {
    return <div className="loading">Loading analytics...</div>
  }

  return (
    <div className="analytics-page">
      <div className="page-header">
        <div>
          <h2>Analytics</h2>
          <p className="subtitle">Overview of your CRM performance</p>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="metrics-grid">
        <div className="metric-card">
          <div className="metric-icon">👥</div>
          <div className="metric-content">
            <div className="metric-value">{totalContacts}</div>
            <div className="metric-label">Total Contacts</div>
          </div>
        </div>

        <div className="metric-card">
          <div className="metric-icon">💼</div>
          <div className="metric-content">
            <div className="metric-value">{totalDeals}</div>
            <div className="metric-label">Total Deals</div>
          </div>
        </div>

        <div className="metric-card">
          <div className="metric-icon">💰</div>
          <div className="metric-content">
            <div className="metric-value">{formatCurrency(totalDealValue)}</div>
            <div className="metric-label">Total Pipeline Value</div>
          </div>
        </div>

        <div className="metric-card">
          <div className="metric-icon">📊</div>
          <div className="metric-content">
            <div className="metric-value">{formatCurrency(averageDealValue)}</div>
            <div className="metric-label">Average Deal Value</div>
          </div>
        </div>

        <div className="metric-card">
          <div className="metric-icon">📝</div>
          <div className="metric-content">
            <div className="metric-value">{totalActivities}</div>
            <div className="metric-label">Total Activities</div>
          </div>
        </div>

        <div className="metric-card">
          <div className="metric-icon">📅</div>
          <div className="metric-content">
            <div className="metric-value">{recentActivities}</div>
            <div className="metric-label">Activities (7 days)</div>
          </div>
        </div>

        <div className="metric-card">
          <div className="metric-icon">🔍</div>
          <div className="metric-content">
            <div className="metric-value">{avgProspectsPerDay}</div>
            <div className="metric-label">Avg Prospects/Day</div>
          </div>
        </div>

        <div className="metric-card">
          <div className="metric-icon">🏢</div>
          <div className="metric-content">
            <div className="metric-value">{avgCompanyProspectsPerDay}</div>
            <div className="metric-label">Avg Company Prospects/Day</div>
          </div>
        </div>

        <div className="metric-card">
          <div className="metric-icon">💼</div>
          <div className="metric-content">
            <div className="metric-value">{avgJobRoleAnalysisPerDay}</div>
            <div className="metric-label">Avg Job Role Analysis/Day</div>
          </div>
        </div>
      </div>

      <div className="analytics-grid">
        {/* Deal Value by Stage */}
        <div className="analytics-card">
          <h3>Deal Value by Stage</h3>
          {dealValueByStage.length > 0 ? (
            <div className="stage-value-list">
              {dealValueByStage.map((stage, idx) => (
                <div key={idx} className="stage-value-item">
                  <div className="stage-value-header">
                    <span className="stage-name">{stage.stageName}</span>
                    <span className="stage-count">{stage.count} deal{stage.count !== 1 ? 's' : ''}</span>
                  </div>
                  <div className="stage-value-amount">{formatCurrency(stage.value)}</div>
                  {totalDealValue > 0 && (
                    <div className="stage-value-bar">
                      <div 
                        className="stage-value-bar-fill" 
                        style={{ width: `${(stage.value / totalDealValue) * 100}%` }}
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p className="empty-state-text">No deals yet</p>
          )}
        </div>

        {/* Top Deals */}
        <div className="analytics-card">
          <h3>Top Deals by Value</h3>
          {topDeals.length > 0 ? (
            <div className="top-deals-list">
              {topDeals.map((deal, idx) => (
                <div key={deal.id} className="top-deal-item">
                  <div className="top-deal-rank">#{idx + 1}</div>
                  <div className="top-deal-info">
                    <div className="top-deal-name">{deal.name}</div>
                    {deal.contactName && (
                      <div className="top-deal-contact">👤 {deal.contactName}</div>
                    )}
                    {isAdmin && (deal.ownerEmail || deal.owner) && (
                      <div className="top-deal-owner">
                        Rep: {deal.ownerEmail || `${String(deal.owner).slice(0, 10)}…`}
                      </div>
                    )}
                  </div>
                  <div className="top-deal-value">{formatCurrency(deal.value)}</div>
                </div>
              ))}
            </div>
          ) : (
            <p className="empty-state-text">No deals with values yet</p>
          )}
        </div>

        {/* Activity Breakdown */}
        <div className="analytics-card">
          <h3>Activity Breakdown</h3>
          {Object.keys(activityBreakdown).length > 0 ? (
            <div className="activity-breakdown">
              {Object.entries(activityBreakdown)
                .sort(([, a], [, b]) => b - a)
                .map(([type, count]) => (
                  <div key={type} className="activity-breakdown-item">
                    <span className="activity-icon">{getActivityIcon(type)}</span>
                    <span className="activity-type">{type.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}</span>
                    <span className="activity-count">{count}</span>
                  </div>
                ))}
            </div>
          ) : (
            <p className="empty-state-text">No activities yet</p>
          )}
        </div>

        {/* Contacts by Tag */}
        <div className="analytics-card">
          <h3>Summary by Tag</h3>
          {allTags.length > 0 ? (
            <div className="tags-breakdown">
              {allTags.map(([tag, count]) => (
                <div key={tag} className="tag-breakdown-item">
                  <span className="tag-name">{tag}</span>
                  <div className="tag-breakdown-bar">
                    <div 
                      className="tag-breakdown-fill" 
                      style={{ width: `${totalContacts > 0 ? (count / totalContacts) * 100 : 0}%` }}
                    />
                  </div>
                  <span className="tag-count">{count}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="empty-state-text">No tagged contacts yet</p>
          )}
        </div>

        {/* LinkedIn Status Breakdown */}
        <div className="analytics-card">
          <h3>Summary by LinkedIn Status</h3>
          {Object.keys(contactsByLinkedInStatus).length > 0 ? (
            <div className="linkedin-status-breakdown">
              {Object.entries(contactsByLinkedInStatus)
                .sort(([, a], [, b]) => b - a)
                .map(([status, count]) => (
                  <div key={status} className="linkedin-status-item">
                    <div className="linkedin-status-header">
                      <span 
                        className="linkedin-status-badge"
                        style={{
                          backgroundColor: getLinkedInStatusColor(status) + '20',
                          color: getLinkedInStatusColor(status)
                        }}
                      >
                        {getLinkedInStatusLabel(status)}
                      </span>
                      <span className="linkedin-status-count">{count}</span>
                    </div>
                    <div className="linkedin-status-bar">
                      <div 
                        className="linkedin-status-bar-fill" 
                        style={{ 
                          width: `${totalContacts > 0 ? (count / totalContacts) * 100 : 0}%`,
                          backgroundColor: getLinkedInStatusColor(status)
                        }}
                      />
                    </div>
                  </div>
                ))}
            </div>
          ) : (
            <p className="empty-state-text">No LinkedIn status data yet</p>
          )}
        </div>
      </div>
    </div>
  )
}

export default Analytics

